import { useState, useEffect, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { parseTemplateContentBlocks, generateSidePanelSections, validateBusinessContext, getContextKeyForContentBlock, getDataForContentBlock } from "../utils/templateParser";
import { getContentBlockConfig } from "../constants/contentBlockConstants";
import { generateContentForContentBlock } from "../utils/contentGenerators";
import { expandDynamicContentBlocks, hasExpandableContentBlocks } from "../utils/dynamicContentBlockExpansion";

const createDefaultFilterState = () => ({
    selectedIds: null,
    sourceIds: [],
    filterCriteria: {},
    updatedAt: null,
});

const normalizeFilterState = (filterState) => {
    if (!filterState || typeof filterState !== "object") {
        return createDefaultFilterState();
    }

    return {
        selectedIds: Array.isArray(filterState.selectedIds) ? filterState.selectedIds : Array.isArray(filterState.selected_ids) ? filterState.selected_ids : null,
        sourceIds: Array.isArray(filterState.sourceIds) ? filterState.sourceIds : Array.isArray(filterState.source_ids) ? filterState.source_ids : [],
        filterCriteria: filterState.filterCriteria || filterState.filter_criteria || {},
        updatedAt: filterState.updatedAt || filterState.updated_at || null,
    };
};

/**
 * 内容块管理Hook
 * 管理内容块实例、可用内容块和侧边栏状态
 */
export const useContentBlocks = (templateContent, businessContext, enableV2Mode) => {
    // V2.5 新增状态：内容块实例管理
    const [contentBlockInstances, setContentBlockInstances] = useState([]);
    // V2 新增状态：可用的内容块定义
    const [availableContentBlocks, setAvailableContentBlocks] = useState([]);
    // 侧边栏配置
    const [sidePanelSections, setSidePanelSections] = useState([]);

    // ⚠️ 关键稳定性处理：businessContext 必须按"值"而非"引用"参与后续所有 memo/effect 依赖。
    // 多数调用方在 JSX 里内联生成上下文（如 businessContext={generateEmailContextData()}），
    // 父组件任意一次 re-render（例如浏览器缩放触发监控页 resize setState）都会传入
    // 内容相同但引用全新的对象。如果直接依赖引用，会级联导致 useEmailEditor 中
    // useEditor 的 deps 变化 → TipTap 编辑器销毁重建 → 用户已编辑的正文全部丢失。
    // 这里用 JSON 序列化签名比较，只有业务数据真正变化时才让新引用生效。
    const businessContextSignature = useMemo(() => {
        try {
            return JSON.stringify(businessContext);
        } catch (e) {
            return null; // 无法序列化（如循环引用）时退回引用比较
        }
    }, [businessContext]);

    const stableBusinessContext = useMemo(
        () => businessContext,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [businessContextSignature ?? businessContext],
    );

    // V3 新增：动态内容块扩展预处理
    const { processedTemplateContent, processedBusinessContext } = useMemo(() => {
        // 检查是否需要进行动态扩展
        if (enableV2Mode && hasExpandableContentBlocks(stableBusinessContext)) {
            const { expandedTemplateContent, expandedBusinessContext } = expandDynamicContentBlocks(templateContent, stableBusinessContext);
            return {
                processedTemplateContent: expandedTemplateContent,
                processedBusinessContext: expandedBusinessContext,
            };
        }

        // 如果不需要扩展，直接返回原始数据
        return {
            processedTemplateContent: templateContent,
            processedBusinessContext: stableBusinessContext,
        };
    }, [templateContent, stableBusinessContext, enableV2Mode]);

    // 解析模板内容（使用预处理后的模板）
    const { tokenizedTemplate, blocks: contentBlocks } = useMemo(() => {
        if (!processedTemplateContent) return { tokenizedTemplate: "", blocks: [] };
        return parseTemplateContentBlocks(processedTemplateContent);
    }, [processedTemplateContent]);

    // V2.5 模式：初始化内容块实例状态（使用预处理后的数据）
    useEffect(() => {
        if (enableV2Mode && processedBusinessContext.contentBlocks) {
            // 从预处理后的 businessContext.contentBlocks 初始化状态
            const availableBlocks = [];

            // V2.5 模式下，解析预处理后的模板中预置的内容块，为每个生成instanceId
            const { blocks: preInsertedBlocks } = parseTemplateContentBlocks(processedTemplateContent);

            setContentBlockInstances((prevInstances) => {
                const reusableInstances = [...prevInstances];
                const nextInstances = [];

                // 为模板中预置的内容块创建实例，并在重建时按 blockId/type 保留筛选状态。
                preInsertedBlocks.forEach((block) => {
                    if (block.id) {
                        // 查找对应的业务数据（在预处理后的上下文中）
                        const businessBlock = processedBusinessContext.contentBlocks.find((b) => b.id === block.id);
                        if (businessBlock) {
                            const reusableIndex = reusableInstances.findIndex((instance) => instance.blockId === block.id && instance.type === businessBlock.type);
                            const reusableInstance = reusableIndex >= 0 ? reusableInstances.splice(reusableIndex, 1)[0] : null;

                            nextInstances.push({
                                instanceId: reusableInstance?.instanceId || uuidv4(),
                                blockId: block.id,
                                type: businessBlock.type,
                                title: businessBlock.title || block.label,
                                data: businessBlock.data,
                                isInserted: true, // 模板中预置的都标记为已插入
                                viewMode: reusableInstance?.viewMode || "summary",
                                filterState: normalizeFilterState(reusableInstance?.filterState || businessBlock.filterState || businessBlock.filter_state),
                                // 存储原始模板块信息，用于后续替换
                                templateBlockInfo: block,
                                // V3 新增：存储扩展信息
                                expansionInfo: businessBlock._expansionInfo || null,
                            });
                        }
                    }
                });

                return nextInstances;
            });

            // 设置可用的内容块定义（使用预处理后的数据）
            processedBusinessContext.contentBlocks.forEach((block) => {
                availableBlocks.push(block);
            });

            setAvailableContentBlocks(availableBlocks);
        }
    }, [enableV2Mode, processedBusinessContext, processedTemplateContent]);

    // 生成初始内容（使用预处理后的模板）
    const initialContent = useMemo(() => {
        if (enableV2Mode) {
            // V2.5 模式：处理预处理后的模板，为预置的内容块注入instanceId
            if (!processedTemplateContent) return "<p>请从右侧选择要插入的内容块。</p>";

            // 解析预处理后的模板并为每个内容块注入instanceId
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = processedTemplateContent;

            const contentBlockElements = tempDiv.querySelectorAll("[data-content-block-type]");
            contentBlockElements.forEach((element) => {
                const blockId = element.getAttribute("data-content-block-id");
                if (blockId) {
                    // 查找对应的实例
                    const instance = contentBlockInstances.find((inst) => inst.blockId === blockId);
                    if (instance) {
                        // 注入instanceId属性
                        element.setAttribute("data-instance-id", instance.instanceId);
                    }
                }
            });

            return tempDiv.innerHTML;
        }

        // V1 模式：保持原有逻辑
        if (!tokenizedTemplate || !stableBusinessContext || Object.keys(stableBusinessContext).length === 0 || contentBlocks.length === 0) {
            return tokenizedTemplate || "<p></p>";
        }

        try {
            const blockTypes = [...new Set(contentBlocks.map((block) => block.type))];
            const validation = validateBusinessContext(blockTypes, stableBusinessContext);

            if (!validation.isValid) {
                console.warn(`业务数据验证失败，无法完全填充模板，缺失的键: ${validation.missing.join(", ")}`);
                return tokenizedTemplate;
            }

            let processedContent = tokenizedTemplate;
            contentBlocks.forEach((block) => {
                const { token, type } = block;
                const contextKey = getContextKeyForContentBlock(type);

                const dataForKey = contextKey ? stableBusinessContext[contextKey] : null;
                const data = getDataForContentBlock(block, dataForKey);

                const generatedHtml = generateContentForContentBlock(type, data);

                if (token) {
                    processedContent = processedContent.replace(token, generatedHtml);
                }
            });

            return processedContent;
        } catch (error) {
            console.error("处理邮件模板时出错:", error);
            return tokenizedTemplate;
        }
    }, [enableV2Mode, processedTemplateContent, tokenizedTemplate, contentBlocks, stableBusinessContext, contentBlockInstances]);

    // 生成侧边栏配置
    useEffect(() => {
        if (enableV2Mode) {
            // V2.5 模式：基于 availableContentBlocks 和 contentBlockInstances 生成侧边栏
            if (availableContentBlocks.length > 0) {
                // 按类型分组可用的内容块
                const blocksByType = availableContentBlocks.reduce((groups, block) => {
                    if (!groups[block.type]) {
                        groups[block.type] = [];
                    }
                    groups[block.type].push(block);
                    return groups;
                }, {});

                const sections = [];

                // 为每个类型创建一个section
                Object.entries(blocksByType).forEach(([type, blocks]) => {
                    const config = getContentBlockConfig(type);
                    if (config) {
                        // 获取该类型的所有实例，只显示已插入的实例
                        const typeInstances = contentBlockInstances
                            .filter((instance) => instance.type === type && instance.isInserted)
                            .map((instance, index) => ({
                                ...instance,
                                displayIndex: index + 1, // 添加显示序号
                            }));

                        sections.push({
                            id: `content-blocks-${type}`,
                            title: config.label,
                            type: "contentBlocks",
                            availableBlocks: blocks, // 可用的内容块定义
                            instances: typeInstances, // 当前的实例列表（只包含已插入的）
                        });
                    }
                });

                setSidePanelSections(sections);
            }
        } else {
            // V1 模式：保持原有逻辑
            if (contentBlocks.length > 0 && processedBusinessContext) {
                const sections = generateSidePanelSections(contentBlocks, processedBusinessContext);
                setSidePanelSections(sections);
            }
        }
    }, [enableV2Mode, availableContentBlocks, contentBlockInstances, contentBlocks, processedBusinessContext]);

    // V2.5 新增：创建新的内容块实例
    const createContentBlockInstance = useCallback(
        (blockId) => {
            const blockData = availableContentBlocks.find((b) => b.id === blockId);
            if (!blockData) return null;

            const instanceId = uuidv4();
            const newInstance = {
                instanceId,
                blockId,
                type: blockData.type,
                title: blockData.title || blockData.type,
                data: blockData.data,
                isInserted: false,
                viewMode: "summary",
                filterState: normalizeFilterState(blockData.filterState || blockData.filter_state),
            };

            setContentBlockInstances((prev) => [...prev, newInstance]);
            return newInstance;
        },
        [availableContentBlocks],
    );

    // V2.5 新增：处理编辑器更新事件
    const handleEditorUpdate = useCallback(
        (editor, transaction) => {
            if (!transaction.docChanged) return;

            // 检测当前文档中的 ContentBlock 节点及其instanceId
            const currentInstanceIds = new Set();

            editor.state.doc.descendants((node) => {
                if ((node.type.name === "contentBlock" || node.type.name === "inlineContentBlock") && node.attrs.instanceId) {
                    currentInstanceIds.add(node.attrs.instanceId);
                }
            });

            // 更新状态：检测哪些实例被删除了，以及新增的实例
            setContentBlockInstances((prevInstances) => {
                let newInstances = [...prevInstances];
                let hasChanges = false;

                // 检查现有实例的状态变化
                newInstances = newInstances.map((instance) => {
                    const wasInserted = instance.isInserted;
                    const isCurrentlyInserted = currentInstanceIds.has(instance.instanceId);

                    if (wasInserted && !isCurrentlyInserted) {
                        // 实例被删除了
                        hasChanges = true;
                        return {
                            ...instance,
                            isInserted: false,
                        };
                    } else if (!wasInserted && isCurrentlyInserted) {
                        // 实例被添加了
                        hasChanges = true;
                        return {
                            ...instance,
                            isInserted: true,
                        };
                    }
                    return instance;
                });

                // 检查是否有新的实例（通过@提及等方式添加的）
                const existingInstanceIds = new Set(newInstances.map((inst) => inst.instanceId));

                editor.state.doc.descendants((node) => {
                    if ((node.type.name === "contentBlock" || node.type.name === "inlineContentBlock") && node.attrs.instanceId && !existingInstanceIds.has(node.attrs.instanceId)) {
                        // 发现新的实例，需要添加到状态中
                        const blockId = node.attrs.contentBlockId;
                        const blockData = availableContentBlocks.find((b) => b.id === blockId);

                        if (blockData) {
                            newInstances.push({
                                instanceId: node.attrs.instanceId,
                                blockId: blockId,
                                type: blockData.type,
                                title: blockData.title || blockData.type,
                                data: blockData.data,
                                isInserted: true,
                                viewMode: node.attrs.viewMode || "summary",
                                filterState: normalizeFilterState(blockData.filterState || blockData.filter_state),
                            });
                            hasChanges = true;
                        }
                    }
                });

                return hasChanges ? newInstances : prevInstances;
            });
        },
        [availableContentBlocks],
    );

    const updateInstanceFilter = useCallback((instanceId, filterState, editor) => {
        const normalizedFilterState = normalizeFilterState(filterState);
        const filterRevision = normalizedFilterState.updatedAt || new Date().toISOString();

        setContentBlockInstances((prevInstances) =>
            prevInstances.map((instance) =>
                instance.instanceId === instanceId
                    ? {
                          ...instance,
                          filterState: normalizedFilterState,
                          filterRevision,
                      }
                    : instance,
            ),
        );

        if (editor?.storage && Array.isArray(editor.storage.contentBlockInstances)) {
            editor.storage.contentBlockInstances = editor.storage.contentBlockInstances.map((instance) =>
                instance.instanceId === instanceId
                    ? {
                          ...instance,
                          filterState: normalizedFilterState,
                          filterRevision,
                      }
                    : instance,
            );
        }

        if (!editor?.state?.doc || !editor?.view) return;

        const { state, view } = editor;
        const { tr } = state;
        let updated = false;

        state.doc.descendants((node, pos) => {
            if ((node.type.name === "contentBlock" || node.type.name === "inlineContentBlock") && node.attrs.instanceId === instanceId) {
                tr.setNodeMarkup(pos, null, {
                    ...node.attrs,
                    filterRevision,
                });
                updated = true;
            }
        });

        if (updated) {
            view.dispatch(tr);
        }
    }, []);

    return {
        contentBlockInstances,
        setContentBlockInstances,
        availableContentBlocks,
        sidePanelSections,
        initialContent,
        tokenizedTemplate,
        contentBlocks,
        createContentBlockInstance,
        handleEditorUpdate,
        updateInstanceFilter,
        // V3 新增：返回扩展后的业务上下文，供主题模板解析使用
        processedBusinessContext,
    };
};
